import { useMutation } from '@apollo/client';
import { useCurrentAccount } from '@application/api';
import { setLoginMethod, useSocialLoginModal } from '@application/components';
import { SocialLoginMethod } from '@application/types';
import { useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type { AccountLoginInput } from '@gql/graphql';
import { useCallback } from 'react';

export const ACCOUNT_LOGIN = graphql(/* GraphQL */ `
  mutation AccountLogin($data: AccountLoginInput!) {
    login(data: $data) {
      token
      account {
        ...CurrentAccountInfo
      }
    }
  }
`);

export interface LoginValues {
  email: string;
  password: string;
}

export const useLogin = () => {
  const { translate, infoFromCurrentPath, navigateToRoute } = useRouter();
  const { showToast } = useShowToast();
  const { currentAccountUpdateCache, currentAccountClear } = useCurrentAccount();
  const {
    modalHook: { show: showUseSocialLoginModal },
  } = useSocialLoginModal();

  const [loginMutation, { loading, error }] = useMutation(ACCOUNT_LOGIN, {
    onError: (err) => {
      currentAccountClear();

      if (['login/use-google', 'login/use-facebook'].includes(err.message)) {
        setLoginMethod(
          err.message === 'login/use-google'
            ? SocialLoginMethod.GOOGLE
            : SocialLoginMethod.FACEBOOK,
        );
        showUseSocialLoginModal();
      }
    },
  });

  const login = useCallback(
    async (data: AccountLoginInput) => {
      const account = await loginMutation({
        variables: { data },
      });
      currentAccountUpdateCache(account.data?.login?.account, account.data?.login?.token);

      if (infoFromCurrentPath?.state?.returnInfo) {
        const { params, route, search } = infoFromCurrentPath.state
          .returnInfo as typeof infoFromCurrentPath;

        return navigateToRoute(route, { params, search });
      }
    },
    [
      currentAccountUpdateCache,
      infoFromCurrentPath?.state?.returnInfo,
      loginMutation,
      navigateToRoute,
    ],
  );

  const loginWithGoogleToken = useCallback(
    (googleToken?: string) => {
      if (!googleToken) {
        return showToast(
          translate('common.toasts.apolloError.auth/google-token-invalid'),
          {
            type: 'error',
          },
          { contactError: 'LoginWithGoogleToken : no google token found' },
        );
      }
      return login({ googleToken });
    },
    [login, showToast, translate],
  );

  const loginWithFacebookToken = useCallback(
    (facebookToken?: string) => {
      if (!facebookToken) {
        return showToast(
          translate('common.toasts.apolloError.auth/facebook-token-invalid'),
          {
            type: 'error',
          },
          { contactError: 'loginWithFacebookToken : no facebook token found' },
        );
      }
      return login({ facebookToken });
    },
    [login, showToast, translate],
  );

  const loginWithEmail = useCallback(
    async (values: LoginValues) => {
      const { email, password } = values;

      return login({ email, password });
    },
    [login],
  );

  return {
    loginWithEmail,
    loginWithGoogleToken,
    loginWithFacebookToken,
    loginLoading: loading,
    loginError: error,
  };
};
